import React from 'react'
import { Typography, Box } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import RichText, { BLOCKS } from '../RichText'
import { HeroTextSectionProps, HTSVariants } from '../../types/heroTextSection'
import useBrand from '../../hooks/useBrand'
import {
  BindToFields,
  CommonClasses,
  MatchClasses,
  SuccessStoryClasses,
  TermsClasses,
} from '../../types/contentfulAttributes'
import useMatchData from '../../hooks/useMatchData'
import RichTextErrorBoundary from '../RichText/ErrorBoundary'
import { createContentfulClassGetter } from '../../utils/contentful'

const HeroTextSectionCentered: React.FC<HeroTextSectionProps> = ({
  displayClass,
  titleClass,
  row,
  sectionClass,
}) => {
  const { classes, cx } = useStyles()
  const brand = useBrand()
  const { isError, match, isLoading } = useMatchData()
  const applyContentfulStyles = createContentfulClassGetter(row.css)

  const isTmHeroSection = row.codeClasses?.includes(MatchClasses.HeroSection)
  const isTmHeroErrorSection = row.codeClasses?.includes(
    MatchClasses.HeroErrorSection
  )
  const isTm3ColSection = row.codeClasses?.includes(
    MatchClasses.ThreeColSection
  )
  const isTm3ColErrorSection = row.codeClasses?.includes(
    MatchClasses.ThreeColSectionForError
  )

  // Don't load the match error sections if
  // 1. the match data is being loaded OR
  // 2. a match is found
  if ((isTmHeroErrorSection || isTm3ColErrorSection) && (isLoading || match)) {
    return null
  }

  // Don't load the data-specific match sections if
  // 1. there's an error OR
  // 2. there is no match after API finishes loading
  if (
    (isTmHeroSection || isTm3ColSection) &&
    (isError || (!isLoading && !match))
  ) {
    return null
  }

  return (
    <Box
      className={cx(displayClass, sectionClass, classes.mainPadding, {
        [classes.container]: row.variant !== HTSVariants.Left,
        [classes.containerLeft]: row.variant === HTSVariants.Left,
      })}
    >
      {row.subtitle && (
        <Typography component="h2" className={titleClass}>
          {row.subtitle}
        </Typography>
      )}
      <Box
        className={cx(
          {
            [classes.innerContainer]: brand.name !== 'ZipJob',
            [classes.container]: row.variant !== HTSVariants.Left,
            [classes.jobServiceContainer]: row.codeClasses?.includes(
              CommonClasses.JobService
            ),
            [classes.containerLeft]: row.variant === HTSVariants.Left,
            [classes.successQuote]: row.codeClasses?.includes(
              SuccessStoryClasses.Quote
            ),
          },
          applyContentfulStyles(BindToFields.BodyHeroTextSection)
        )}
      >
        <RichText
          rawBody={row.body.raw}
          references={row.body.references}
          options={{
            props: {
              [BLOCKS.HEADING_1]: {
                className: cx({
                  [classes.narrowPadding]: brand.name === 'ZipJob',
                  [classes.narrow]: !HTSVariants.Left,
                }),
              },
              [BLOCKS.HEADING_2]: {
                className: cx({
                  [classes.narrowPadding]: brand.name === 'ZipJob',
                  [classes.narrow]: !HTSVariants.Left,
                }),
              },
              [BLOCKS.HR]: {
                className: classes.hr,
              },
              [BLOCKS.PARAGRAPH]: {
                className: cx({
                  [classes.text]: true,
                  [classes.marginLeft]: row.variant === HTSVariants.Left,
                  [classes.marginCenter]:
                    row.variant !== HTSVariants.Left &&
                    !sectionClass?.includes(TermsClasses.Hero) &&
                    !sectionClass?.includes(CommonClasses.Contact) &&
                    !sectionClass?.includes(CommonClasses.PrivacyPolicyHero),
                  [classes.marginCenterZipJob]:
                    brand.name === 'ZipJob' && row.variant !== HTSVariants.Left,
                  [classes.narrowPadding]: brand.name === 'ZipJob',
                  [classes.narrow]:
                    row.variant === HTSVariants.CenteredSkinny ||
                    row.variant === HTSVariants.CenteredVideo,
                  [classes.narrowPara]: row.variant === HTSVariants.Centered,
                }),
              },
            },
            renderNode: {
              [BLOCKS.TABLE]: (node, children) => (
                <RichTextErrorBoundary node={node}>
                  <Box className={classes.tableWrapper}>
                    <table>{children}</table>
                  </Box>
                </RichTextErrorBoundary>
              ),
            },
          }}
          cssEntries={row.css}
        />
      </Box>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  jobServiceContainer: {
    '& > div': {
      width: '76%',
      gap: '1rem',
      [theme.breakpoints.down('md')]: {
        gap: 0,
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
      '& > div > div, & > div > div > ul': {
        width: '100%',
      },
      '& > div': {
        flex: 1,
        '& > div': {
          margin: '0',
        },
      },
    },
  },
  innerContainer: {
    margin: '0 auto',

    '@media (min-width: 769px) and (max-width: 1024px)': {
      maxWidth: '90%',
    },
  },
  containerLeft: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  marginLeft: {
    '& > a:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('md')]: {
        margin: '1rem 0',
      },
    },
    '& > button:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('md')]: {
        margin: '1rem 0',
      },
    },
    '& > a': {
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
    '& > button': {
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
  },
  marginCenter: {
    '& > a:nth-child(n)': {
      margin: '0',
      [theme.breakpoints.down('md')]: {
        margin: '0.4rem 0',
      },
    },
    '& > button:nth-child(n)': {
      margin: '0',
      [theme.breakpoints.down('md')]: {
        margin: '0.4rem 0',
      },
    },
    '& > a:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('md')]: {
        margin: '1rem 0',
      },
    },
    '& > button:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('md')]: {
        margin: '1rem 0',
      },
    },
    '& > a': {
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
    '& > button': {
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
  },
  marginCenterZipJob: {
    '& > a:nth-child(n)': {
      margin: '1rem 0',
      [theme.breakpoints.down('md')]: {
        margin: '0.4rem 0',
      },
    },
    '& > button:nth-child(n)': {
      margin: '1rem 0',
      [theme.breakpoints.down('md')]: {
        margin: '0.4rem 0',
      },
    },
    '& > a:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('md')]: {
        margin: '1rem 0',
      },
    },
    '& > button:nth-child(2)': {
      marginLeft: '1rem',
      [theme.breakpoints.down('md')]: {
        margin: '1rem 0',
      },
    },
    '& > a': {
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
    '& > button': {
      [theme.breakpoints.down('md')]: {
        marginLeft: '0',
        width: '100%',
      },
    },
  },
  mainPadding: {
    paddingBottom: '4rem',
    paddingTop: '4rem',
  },
  narrow: {
    paddingLeft: '16%',
    paddingRight: '16%',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '0.34375rem',
      paddingRight: '0.34375rem',
      width: '100%',
    },
  },
  narrowPadding: {
    paddingBottom: '0.5625rem',
    paddingTop: '0.5625rem',
    [theme.breakpoints.down('md')]: {
      paddingBottom: '0.34375rem',
      paddingTop: '0.34375rem',
    },
  },
  narrowPara: {
    paddingLeft: 'calc((100% - 38.5em)/2)',
    paddingRight: 'calc((100% - 38.5em)/2)',
    display: 'contents',
    [theme.breakpoints.down('md')]: {
      paddingLeft: '1rem',
      paddingRight: '1rem',
    },
  },
  paddingBottomZero: {
    paddingBottom: '0',
  },
  hr: {
    margin: '1em 0',
    width: '100%',
  },
  text: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  successQuote: {
    maxWidth: '35rem',
    margin: '0 auto',
  },
  tableWrapper: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '1rem',
    width: '100%',
    overflow: 'auto',
  },
}))
export default HeroTextSectionCentered
